import React from 'react'

// Display-layer text sanitizer for course content.
// Eliminates em-dashes (—), en-dashes (–), and horizontal bars (―) which are classic AI tells.
export function cleanCourseText(raw: string | null | undefined): string {
  if (!raw) return ''
  let s = String(raw)
  // 1. Decode HTML dash entities if present
  s = s.replace(/&(?:mdash|#8212|#x2014);/gi, ' - ')
       .replace(/&(?:ndash|#8211|#x2013);/gi, ' - ')
  // 2. Strip redundant leading module/lesson/unit labels (e.g. "Module 1: ", "Unit 3 - ")
  s = s.replace(/^(?:Module|Unit|Lesson)\s*\d+\s*[:\-–—―]+\s*/i, '')
  // 3. Replace any spaced or unspaced em/en dashes and quotation dashes with standard hyphen " - "
  s = s.replace(/\s*[—–―‒]\s*/g, ' - ')
  // 4. Any remaining stray em-dashes or en-dashes become standard "-"
  s = s.replace(/[—–―‒]/g, '-')
  // 5. Clean up doubled dashes, punctuation combinations, and redundant whitespace
  s = s.replace(/-\s*-+/g, '-')
       .replace(/\s*-\s*:\s*/g, ': ')
       .replace(/:\s*-\s*/g, ': ')
       .replace(/\s*,\s*-\s*/g, ', ')
       .replace(/\s*-\s*,\s*/g, ', ')
       .replace(/\s{2,}/g, ' ')
       .replace(/^[,\s:\-]+|[,\s:\-]+$/g, '')
  return s.trim()
}

/**
 * Strips markdown syntax like **bold**, *italic*, # headers, etc. to clean plain text.
 */
export function stripMarkdown(raw: string | null | undefined): string {
  if (!raw) return ''
  return cleanCourseText(raw)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#+\s+/gm, '')
    .trim()
}

/**
 * Parses markdown inline bold (**text**) and renders real <strong> elements instead
 * of printing raw asterisks. Also applies cleanCourseText.
 */
export function renderFormattedText(raw: string | null | undefined): React.ReactNode {
  if (!raw) return null
  const cleaned = cleanCourseText(raw)
  const parts = cleaned.split(/(\*\*.*?\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return React.createElement('strong', { key: i, style: { fontWeight: 800 } }, part.slice(2, -2))
    }
    return part
  })
}

/**
 * Age is contextual: in business/university/church/adult organisations, displaying
 * "Age 5+" or "Age 12+" causes confusion because the AI dynamically adapts instruction
 * to the learner's profile. Only show age for primary/secondary K-12 school and homeschool contexts.
 */
export function shouldShowAge(orgType?: string | null): boolean {
  if (!orgType) return false
  return orgType === 'school' || orgType === 'family_edu'
}